/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import UserIcon from "@/components/custom/icons/user";
import { Typography } from "@/components/custom/typography";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useCommonCDByCodes } from "@/hooks/useCommonCD";
import { useTypedTranslations } from "@/i18n/typed";
import { PROFILE } from "@/lib/constants";
import { groupDataByKey } from "@/lib/utils";
import { UserType } from "@/types/user";
import Link from "next/link";
import { useCallback, useMemo } from "react";
import LocaleSwitcher from "./LocaleSwitcher";
import Logout from "./Logout";

export function DropdownProfile({ userInfo }: { userInfo: UserType }) {
  const { data: cmmcRes } = useCommonCDByCodes(["org_type"]);
  const commonCDData = cmmcRes?.data;

  const organizations_type = useMemo(
    () => (commonCDData ? groupDataByKey(commonCDData, "grp_sub_cd") : {}),
    [commonCDData]
  );
  type ProfileKey = keyof typeof PROFILE;
  const USER_TYPE_TO_PROFILE_KEY: Record<string, ProfileKey> = {
    advertiser: "advertiser",
    media: "media",
    platform: "platform",
  };
  const profileKey: ProfileKey =
    USER_TYPE_TO_PROFILE_KEY[userInfo.user_type ?? "advertiser"] ??
    "advertiser";

  const profileItems = PROFILE[profileKey];

  const tProfile = useTypedTranslations("dropDown");

  // Prevent popover from closing when interacting with map elements
  const handleInteractOutside = useCallback(
    (event: { target: EventTarget | null; preventDefault: () => void }) => {
      const target = event.target as HTMLElement | null;
      if (
        target?.closest('[class*="gm-"]') ||
        target?.closest('[class*="custom-pin"]') ||
        target?.closest('canvas') ||
        target?.closest('[data-slot="map"]')
      ) {
        event.preventDefault();
      }
    },
    []
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <UserIcon size={30} data-testid="user-avatar" />
      </PopoverTrigger>

      <PopoverContent
        sideOffset={20}
        align="end"
        className="w-[232px] rounded-2xl shadow-xl/30 border-0 p-4"
        onInteractOutside={handleInteractOutside}
      >
        <div className="grid gap-4">
          {/* User info */}
          <div className="space-y-1">
            <Typography as="h4" className="text-primary-700">
              {userInfo.name}
            </Typography>

            <Typography variant="body4">
              <Typography as="span" variant="detail1">
                {userInfo.organizations_name}
                {userInfo.organizations_type && (
                  <>
                    {" "}
                    (
                    {organizations_type?.[userInfo.organizations_type]?.[0]
                      ?.common_cd_name || userInfo.organizations_type}
                    )
                  </>
                )}
              </Typography>
            </Typography>
          </div>

          {/* Profile menu */}
          <div className="grid text-gray-800 font-semibold gap-2">
            {profileItems.map((item: any) => (
              <Link href={item.url} key={item.url}>
                {tProfile(`profile.${item.labelKey}` as any)}
              </Link>
            ))}
          </div>

          {/* Language */}
          <div className="flex gap-2 items-center">
            <Typography as="span">Language</Typography>
            <LocaleSwitcher className="!h-9 w-full" hiddenTextPrefix />
          </div>

          {/* Logout */}
          <div className="flex justify-start">
            <Logout />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
